import type { ActivatedRouteSnapshot } from '@angular/router';
import type { BreadcrumbItem, RouteBreadcrumbDef } from '../breadcrumb/breadcrumb.models';

/**
 * Percorre a árvore de rotas a partir do snapshot raiz e monta a trilha de breadcrumb.
 *
 * Regras:
 * - Lê `snapshot.data['breadcrumb']` para compatibilidade com resolvers.
 * - Rotas com `path: ''` (sem segmentos próprios) só contribuem se tiverem
 *   `breadcrumb` explícito em `routeConfig.data` — evita herança do pai.
 * - `{ skip: true }` ignora a rota sem quebrar a cadeia.
 * - Funções recebem o snapshot e retornam string/BreadcrumbItem ou null/undefined (ignora).
 * - Itens intermediários recebem `url` acumulado se não informado explicitamente.
 * - O último item nunca recebe `url` (página atual, sem link).
 */
export function buildRouteBreadcrumbs(root: ActivatedRouteSnapshot): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];
  let node: ActivatedRouteSnapshot | null = root;

  while (node) {
    // Rotas com path vazio não adicionam segmentos à URL e podem herdar o data.breadcrumb
    // do pai. Só as aceita se o routeConfig.data define explicitamente um breadcrumb.
    const hasOwnSegments = node.url.length > 0;
    const hasExplicitConfig =
      !!node.routeConfig?.data && 'breadcrumb' in node.routeConfig.data;

    if (!hasOwnSegments && !hasExplicitConfig) {
      node = node.firstChild;
      continue;
    }

    const def: RouteBreadcrumbDef | undefined = node.data['breadcrumb'];

    if (def !== undefined && def !== null) {
      if (typeof def === 'object' && 'skip' in def && def.skip) {
        node = node.firstChild;
        continue;
      }

      const resolved = resolveDef(def, node);
      if (resolved) {
        const url = buildUrl(node);
        const item: BreadcrumbItem =
          typeof resolved === 'string' ? { label: resolved, url } : { url, ...resolved };
        items.push(item);
      }
    }

    node = node.firstChild;
  }

  // Último item = página atual: sem link.
  if (items.length > 0) {
    const last = items[items.length - 1];
    items[items.length - 1] = { ...last, url: undefined, queryParams: undefined };
  }

  return items;
}

function resolveDef(
  def: RouteBreadcrumbDef,
  snapshot: ActivatedRouteSnapshot,
): string | BreadcrumbItem | null {
  if (typeof def === 'function') {
    return def(snapshot) ?? null;
  }
  if (typeof def === 'string') return def;
  if ('skip' in def) return null;
  return def;
}

/** Acumula os segmentos de URL a partir da raiz, ignorando segmentos vazios. */
function buildUrl(snapshot: ActivatedRouteSnapshot): string {
  const segments = snapshot.pathFromRoot
    .flatMap(s => s.url)
    .map(u => u.path)
    .filter(p => p.length > 0);
  return '/' + segments.join('/');
}
